// SessionCard.jsx - Individual session card component for PastSessions
import React from "react";
import { Bot, CheckCircle2, Clock, Share2, X } from "lucide-react";
import { API_BASE } from "../../config/api";
import './ModalComponents.css';
import CrownIcon from "../common/CrownIcon";
import { getPreferenceIcon } from "../common/PreferenceIcons";

/**
 * SessionCard - Displays a single game session with participants, scores, and actions
 *
 * @param {Object}   props
 * @param {Object}   props.session - Session data object
 * @param {Object}   props.user - Current user object
 * @param {Function} props.onEdit - Callback when card is clicked (edit mode)
 * @param {Function} props.onRemove - Callback when remove button is clicked
 * @param {Function} props.getParticipantDisplayName - Function to get participant display name
 * @param {Array}    props.inviteStatuses - Invite records for this session [{invite_id, guest_name, token, status, claimed_by_username}]
 * @param {Function} props.onResendInvite - Called with invite record when pending badge is clicked
 * @param {Function} [props.onShare] - Called with the session when the share button is clicked; button hidden if omitted
 */
function SessionCard({
  session,
  user,
  onEdit,
  onRemove,
  getParticipantDisplayName,
  inviteStatuses = [],
  onResendInvite,
  onShare
}) {
  const handleClick = () => {
    if (session.id !== -1) {
      onEdit(session);
    }
  };

  const handleRemoveClick = (e) => {
    e.stopPropagation();
    onRemove(session, e);
  };

  const handleShareClick = (e) => {
    e.stopPropagation();
    onShare(session);
  };

  // Check if current user is the session owner (first participant)
  const isOwner = user &&
    session.participants &&
    session.participants.length > 0 &&
    session.participants[0].user_id === user.id;

  // Check if current user is a participant but not the owner
  const isParticipant = user &&
    session.participants &&
    session.participants.some(p => p.user_id === user.id);

  const canRemoveSelf = isParticipant && !isOwner;
  const showRemoveButton = (isOwner || canRemoveSelf) && session.id !== -1;

  // Get current user's rating from their participant row
  const userParticipant = session.participants?.find(p => p.user_id === user?.id);
  const userRating = userParticipant?.rating;

  // Determine if current user is the winner using winners array
  const isUserWinner = (() => {
    if (!user || !session.winners || !Array.isArray(session.winners)) {
      return false;
    }

    // Simple check: user is in winners array (excluding system dummy user 0)
    return session.winners.includes(user.id) && user.id !== 0;
  })();

  return (
    <div
      onClick={handleClick}
      className={session.id !== -1 ? "session-card-interactive" : "session-card-static"}
      data-game-id={session.game_id}
      data-game-name={session.game_name}
      style={{
        display: "flex",
        gap: "0.75rem",
        padding: "0.75rem 0.75rem 1rem 0.75rem",
        border: "1px solid var(--border-color, #ddd)",
        borderRadius: "8px",
        backgroundColor: "var(--bg-secondary, #f9f9f9)",
        transition: "all 0.2s ease",
        cursor: session.id === -1 ? "default" : "pointer",
        minHeight: "90px",
        position: "relative"
      }}
    >
      {/* Game Image with Overlays */}
      <div style={{ position: "relative", display: "inline-block" }}>
        <img
          src={session.game_id ? `${API_BASE}/games/${session.game_id}/thumbnail` : "/placeholder-game.png"}
          alt={session.game_name}
          style={{
            width: "50px",
            height: "50px",
            objectFit: "cover",
            borderRadius: "6px",
            flexShrink: 0
          }}
        />

        {/* Crown Icon for Winner */}
        {isUserWinner && <CrownIcon size={20} />}

        {/* Win/Loss Banner */}
        {session.win_loss !== undefined && session.win_loss !== null && session.win_loss !== "" && (
          <div className="win-loss-banner" style={{
            position: "absolute",
            bottom: "-8px",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "2px 6px",
            borderRadius: "10px",
            fontSize: "0.65rem",
            fontWeight: "700",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            zIndex: 2,
            backgroundColor: session.win_loss === "Win"
              ? "var(--pista-button-bg, #4caf50)"
              : "var(--error-color, #f44336)",
            color: "var(--pista-button-text, white)",
            pointerEvents: "auto"
          }}>
            {session.win_loss}
          </div>
        )}

        {/* Sample Indicator */}
        {session.id === -1 && (
          <div className="sample-sticker" style={{
            position: "absolute",
            top: "50%",
            left: "0",
            right: "0",
            transform: "translateY(-50%)",
            padding: "4px 8px",
            fontSize: "0.7rem",
            fontWeight: "700",
            textTransform: "uppercase",
            letterSpacing: "1px",
            zIndex: 2,
            backgroundColor: "var(--warning-color, #ff9800)",
            color: "var(--button-primary-text, white)",
            pointerEvents: "auto",
            textAlign: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
          }}>
            Sample
          </div>
        )}

        {/* Rating Sticker */}
        {userRating && (
          <div className="rating-sticker" style={{
            position: "absolute",
            top: "-6px",
            right: "-6px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            zIndex: 2,
            border: "1px solid var(--bg-primary, white)",
            pointerEvents: "auto",
            fontSize: "1rem",
            lineHeight: "1",
            textAlign: "center",
            minWidth: "24px",
            minHeight: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            backgroundColor: "var(--pista-green-primary, #4caf50)"
          }}>
            {getPreferenceIcon(userRating)}
          </div>
        )}
      </div>

      {/* Share Button - shares this session's game image + participants/scores */}
      {onShare && session.id !== -1 && (
        <button
          onClick={handleShareClick}
          className="share-session-btn"
          style={{
            position: "absolute",
            top: "8px",
            right: "40px",
            left: "auto",
            backgroundColor: "var(--pista-green-primary, #2E7D32)",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: "24px",
            height: "24px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3,
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            pointerEvents: "auto",
            transition: "background-color 0.2s ease"
          }}
          title="Share this session"
          aria-label="Share this session"
        >
          <Share2 size={13} />
        </button>
      )}

      {/* Remove Session Button - Owner removes session; non-owner participants leave */}
      {showRemoveButton && (
        <button
          onClick={handleRemoveClick}
          className="remove-session-btn"
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            backgroundColor: "rgba(220, 53, 69, 0.9)",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: "24px",
            height: "24px",
            fontSize: "12px",
            fontWeight: "bold",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3,
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            pointerEvents: "auto",
            transition: "background-color 0.2s ease"
          }}
          title={isOwner ? "Remove session" : "Leave session"}
        >
          <X size={14} />
        </button>
      )}

      {/* Session Details */}
      <div style={{ flex: 1, minWidth: 0, backgroundColor: "var(--bg-secondary, white)", padding: "0.75rem", borderRadius: "8px" }}>
        <h5 style={{
          margin: "0 0 0.15rem 0",
          color: "var(--text-primary, #333)",
          fontSize: "0.9rem",
          fontWeight: "600",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}>
          {session.game_name}
        </h5>
        <p style={{
          margin: "0 0 0.15rem 0",
          fontSize: "0.75rem",
          color: "var(--text-secondary, #666)"
        }}>
          {session.time_display}
        </p>

        {/* Participants and Scores */}
        {session.participants && session.participants.length > 0 && (
          <div style={{
            margin: "0.1rem 0",
            fontSize: "0.75rem",
            color: "var(--text-secondary, #666)"
          }}>
            <div style={{ fontWeight: "bold", marginBottom: "0.1rem", fontSize: "0.7rem" }}>
              Players ({session.participants.length}):
            </div>
            <div className="session-card-participants">
              {session.participants.slice(0, 3).map((participant, pIndex) => (
                <div key={pIndex} className="session-card-participant">
                  {participant.color && (
                    <span
                      className="session-card-participant__dot"
                      style={{
                        backgroundColor:
                          participant.color === "V" ? "var(--participant-violet, #9400D3)" :
                          participant.color === "I" ? "var(--participant-indigo, #4B0082)" :
                          participant.color === "B" ? "var(--participant-blue, #0000FF)" :
                          participant.color === "G" ? "var(--participant-green, #00FF00)" :
                          participant.color === "Y" ? "var(--participant-yellow, #FFFF00)" :
                          participant.color === "O" ? "var(--participant-orange, #FFA500)" :
                          participant.color === "R" ? "var(--participant-red, #FF0000)" :
                          "var(--participant-gray, #808080)",
                      }}
                      aria-hidden="true"
                    />
                  )}
                  {participant.isAutomata && (
                    <Bot
                      size={12}
                      strokeWidth={2.75}
                      className="session-card-participant__bot-icon"
                      aria-label="Automa/Dummy"
                    />
                  )}
                  <span className="session-card-participant__name">
                    {getParticipantDisplayName(participant, pIndex)}
                  </span>
                  {participant.score !== undefined && participant.score !== null && (
                    <span className="session-card-participant__score">{participant.score}</span>
                  )}
                </div>
              ))}
              {session.participants.length > 3 && (
                <div style={{
                  fontSize: "0.65rem",
                  color: "var(--text-secondary, #999)",
                  fontStyle: "italic",
                  textAlign: "center",
                  padding: "0.1rem"
                }}>
                  +{session.participants.length - 3} more...
                </div>
              )}
            </div>
          </div>
        )}

        {/* If no participants, show that info */}
        {(!session.participants || session.participants.length === 0) && (
          <p style={{
            margin: "0.25rem 0",
            fontSize: "0.8rem",
            color: "var(--text-secondary, #666)",
            fontStyle: "italic"
          }}>
            No participant data recorded
          </p>
        )}

        {/* Invite status badges */}
        {inviteStatuses.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginTop: "0.4rem" }}>
            {inviteStatuses.map((inv) => (
              inv.status === "claimed" ? (
                <span
                  key={inv.invite_id}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "3px",
                    padding: "2px 7px",
                    borderRadius: "10px",
                    fontSize: "0.65rem",
                    fontWeight: "600",
                    backgroundColor: "rgba(76,175,80,0.15)",
                    color: "var(--pista-button-bg, #4caf50)",
                    border: "1px solid rgba(76,175,80,0.3)"
                  }}
                >
                  <CheckCircle2 size={12} /> {inv.claimed_by_username || inv.guest_name} joined
                </span>
              ) : (inv.status === "pending" || inv.status === "skipped") ? (
                <button
                  key={inv.invite_id}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onResendInvite && onResendInvite(inv); }}
                  title="Resend invite"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "3px",
                    padding: "2px 7px",
                    borderRadius: "10px",
                    fontSize: "0.65rem",
                    fontWeight: "600",
                    backgroundColor: "rgba(255,152,0,0.12)",
                    color: "var(--warning-color, #f57c00)",
                    border: "1px solid rgba(255,152,0,0.35)",
                    cursor: "pointer",
                    background: "rgba(255,152,0,0.12)"
                  }}
                >
                  <Clock size={12} /> {inv.guest_name} invited
                </button>
              ) : null
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SessionCard;
